import React, { useEffect, useRef, useState } from 'react';
import { 
  fetchParentMessages, 
  fetchAllParentMessages,
  fetchParentMessagesPaginated,
  addParentMessage, 
  uploadParentChatImage, 
  deleteParentMessage,
  getParentUsers,
  getAllStudents,
  subscribeToParentMessages,
  ParentMessage,
  ParentUser
} from '../services/parentMessageService';
import { useAuth } from '../context/AuthContext';
import { formatTime, getRelativeDateLabel, isDifferentDay, sortMessagesByTime } from '../lib/dateUtils';
import DateSeparator from '../components/common/DateSeparator';

const ParentChatPage: React.FC = () => {
  const { user } = useAuth();
  const [showDenied, setShowDenied] = React.useState(user?.role !== 'admin');
  const [errorMsg] = React.useState('Access Denied: Only admins can access the Parent Chat.');

  if (showDenied) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-lg flex flex-col items-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-700 mb-6">{errorMsg}</p>
          <button
            className="px-6 py-2 bg-pink-600 text-white rounded hover:bg-pink-700"
            onClick={() => setShowDenied(false)}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const [parentUsers, setParentUsers] = useState<ParentUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ParentUser | null>(null);
  const [messages, setMessages] = useState<ParentMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fullscreenImg, setFullscreenImg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [optionsOpenId, setOptionsOpenId] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const unsubscribeListRef = useRef<(() => void) | null>(null);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [allStudents, setAllStudents] = useState<ParentUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newChatSearchQuery, setNewChatSearchQuery] = useState('');

  // Load parent users
  const loadParentUsers = async () => {
    setLoadingUsers(true);
    try {
      console.log('🔍 Loading parent users...');
      const users = await getParentUsers();
      console.log('📋 Parent users received from DB:', users);
      console.log('📊 Total parent users count:', users.length);
      
      // Log each user for detailed inspection
      users.forEach((user, index) => {
        console.log(`👤 Parent ${index + 1}:`, {
          userId: user.userId,
          studentName: user.studentName,
          fatherName: user.fatherName,
          level: user.level,
          group: user.group,
          email: user.email
        });
      });
      
      setParentUsers(users);
    } catch (error) {
      console.error('❌ Error loading parent users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load all students for new chat dialog
  const loadAllStudents = async () => {
    try {
      const students = await getAllStudents();
      setAllStudents(students);
    } catch (error) {
      console.error('Error loading all students:', error);
    }
  };

  // Filter parent users based on search query
  const filteredParentUsers = parentUsers.filter(user =>
    user.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.level.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.group.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Add logging for filtering
  React.useEffect(() => {
    console.log('🔄 Parent users state updated:', parentUsers);
    console.log('🔍 Current search query:', searchQuery);
    console.log('📋 Filtered parent users:', filteredParentUsers);
    console.log('📊 Filtered count:', filteredParentUsers.length);
  }, [parentUsers, filteredParentUsers, searchQuery]);

  // Filter students for new chat dialog
  const filteredStudents = allStudents.filter(student =>
    student.studentName.toLowerCase().includes(newChatSearchQuery.toLowerCase()) ||
    student.level.toLowerCase().includes(newChatSearchQuery.toLowerCase()) ||
    student.group.toLowerCase().includes(newChatSearchQuery.toLowerCase()) ||
    student.fatherName.toLowerCase().includes(newChatSearchQuery.toLowerCase())
  );

  // Start new conversation
  const startNewConversation = async (student: ParentUser) => {
    try {
      // Send initial message
      await addParentMessage(`Hello, ${student.fatherName}`, student.userId, false);

      // Add to parent users if not already present
      if (!parentUsers.some(user => user.userId === student.userId)) {
        const currentTime = new Date().toISOString();
        setParentUsers(prev => {
          const updatedUsers = [...prev, { ...student, lastMessageTime: currentTime }];
          // Sort by most recent message (descending - newest first)
          return updatedUsers.sort((a, b) => {
            const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
            const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
            return timeB - timeA;
          });
        });
      }

      // Select the new conversation
      setSelectedUser(student);

      // Close dialog and reset search
      setShowNewChatDialog(false);
      setNewChatSearchQuery('');
    } catch (error) {
      console.error('Error starting new conversation:', error);
    }
  };

  // Load messages for selected user
  const loadMessages = async (userId?: string) => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const docs = await fetchParentMessages(userId);
      const fetchedMsgs: ParentMessage[] = (docs as any[]).map(doc => ({
        $id: doc.$id,
        message: doc.message || '',
        img: doc.img,
        time: doc.time,
        userId: doc.userId,
        isUser: doc.isUser,
      }));
      
      // Sort messages by time
      const sortedMessages = sortMessagesByTime(fetchedMsgs, docs);
      setMessages(sortedMessages);
    } catch (e) {
      console.error('Error loading messages:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadParentUsers();
    loadAllStudents();

    // Subscribe to all new messages to update parent users list ordering
    const unsubscribeList = subscribeToParentMessages((newMessage) => {
      // Update the lastMessageTime for this user and re-sort
      setParentUsers(prevUsers => {
        const updatedUsers = prevUsers.map(user => {
          if (user.userId === newMessage.userId) {
            return {
              ...user,
              lastMessageTime: newMessage.time || new Date().toISOString()
            };
          }
          return user;
        });

        // Re-sort by most recent message (descending - newest first)
        return updatedUsers.sort((a, b) => {
          const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
          const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
          return timeB - timeA;
        });
      });
    });

    unsubscribeListRef.current = unsubscribeList;

    // Cleanup function for component unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (unsubscribeListRef.current) {
        unsubscribeListRef.current();
        unsubscribeListRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadMessages(selectedUser.userId);
      
      // Clean up previous subscription
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      // Subscribe to all messages but filter for the selected user
      const unsubscribe = subscribeToParentMessages(
        (newMessage) => {
          // Only update if the message is for the currently selected user
          if (newMessage.userId === selectedUser.userId) {
            setMessages(prevMessages => {
              // Check if message already exists to avoid duplicates
              const messageExists = prevMessages.some(msg => msg.$id === newMessage.$id);
              if (messageExists) {
                return prevMessages;
              }
              
              // Add new message and sort by time
              const updatedMessages = [...prevMessages, newMessage].sort((a, b) => {
                const timeA = new Date(a.time || a.$createdAt || '').getTime();
                const timeB = new Date(b.time || b.$createdAt || '').getTime();
                return timeA - timeB;
              });
              
              return updatedMessages;
            });
          }
        }
      );
      
      unsubscribeRef.current = unsubscribe;
    } else {
      // Clean up subscription when no user is selected
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    }
    
    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [selectedUser]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || (!newMessage.trim() && !imgUrl)) return;
    
    let uploadedImgUrl = imgUrl;
    if (imageFile) {
      setUploading(true);
      try {
        uploadedImgUrl = await uploadParentChatImage(imageFile);
        setImgUrl(uploadedImgUrl);
      } catch (err) {
        console.error('Error uploading image:', err);
      }
      setUploading(false);
    }
    
    try {
      // Use selectedUser.userId (their email) and set isUser to false since we're admin
      await addParentMessage(newMessage, selectedUser.userId, false, uploadedImgUrl || undefined);
      setNewMessage('');
      setImgUrl('');
      setImageFile(null);
      setImagePreview(null);
    } catch (e) {
      console.error('Error sending message:', e);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImgUrl('');
  };

  // Fullscreen image modal
  const FullscreenImageModal = ({ src, onClose }: { src: string; onClose: () => void }) => (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] p-4" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white hover:text-gray-300"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={src}
          alt="Full preview"
          className="max-w-full max-h-[80vh] object-contain rounded shadow-lg"
        />
      </div>
    </div>
  );

  return (
    <div className="h-full flex bg-gray-100">
      {fullscreenImg && <FullscreenImageModal src={fullscreenImg} onClose={() => setFullscreenImg(null)} />}
      
             {/* Left sidebar - Student list */}
       <div className="w-80 bg-white border-r border-gray-200 flex flex-col h-full">
         <div className="p-4 border-b border-gray-200 flex-shrink-0">
           <div className="flex items-center justify-between mb-2">
             <h2 className="text-xl font-semibold text-gray-800">Parent Chat</h2>
             <button
               onClick={() => setShowNewChatDialog(true)}
               className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors"
             >
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
               </svg>
             </button>
           </div>
           <p className="text-sm text-gray-600 mb-3">Select a student to chat with their parent</p>
           
           {/* Search input */}
           <div className="relative">
             <input
               type="text"
               placeholder="Search conversations..."
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
               className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
             />
             <svg className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
             </svg>
           </div>
         </div>
        
         <div className="flex-1 overflow-y-auto min-h-0">
           {loadingUsers ? (
             <div className="p-4 text-center text-gray-500">Loading students...</div>
           ) : filteredParentUsers.length === 0 ? (
             <div className="p-4 text-center text-gray-500">
               {searchQuery ? 'No conversations found' : 'No students found'}
             </div>
           ) : (
             <div className="divide-y divide-gray-200">
               {filteredParentUsers.map((parent) => (
                 <div
                   key={parent.userId}
                   onClick={() => setSelectedUser(parent)}
                   className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                     selectedUser?.userId === parent.userId ? 'bg-pink-50 border-r-2 border-blue-500' : ''
                   }`}
                 >
                   <div className="flex items-center">
                     {parent.studentPhotoUrl ? (
                       <img 
                         src={parent.studentPhotoUrl} 
                         alt={parent.studentName}
                         className="w-10 h-10 rounded-full object-cover"
                       />
                     ) : (
                       <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                         {parent.studentName.charAt(0).toUpperCase()}
                       </div>
                     )}
                     <div className="ml-3 flex-1">
                       <h3 className="font-medium text-gray-900">{parent.studentName}</h3>
                       <p className="text-sm text-gray-500">Level {parent.level} • {parent.group}</p>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           )}
         </div>
      </div>

      {/* Right side - Chat area */}
      <div className="flex-1 flex flex-col">
        {selectedUser ? (
          <>
                         {/* Chat header */}
             <div className="bg-white border-b border-gray-200 p-4">
               <div className="flex items-center">
                 {selectedUser.studentPhotoUrl ? (
                   <img 
                     src={selectedUser.studentPhotoUrl} 
                     alt={selectedUser.studentName}
                     className="w-10 h-10 rounded-full object-cover"
                   />
                 ) : (
                   <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                     {selectedUser.studentName.charAt(0).toUpperCase()}
                   </div>
                 )}
                 <div className="ml-3">
                   <h3 className="font-medium text-gray-900">{selectedUser.fatherName}</h3>
                   <p className="text-sm text-gray-500">
                     {selectedUser.fatherNum && `📞 ${selectedUser.fatherNum}`}
                     {selectedUser.fatherNum && selectedUser.motherNum && ' • '}
                     {selectedUser.motherNum && `📱 ${selectedUser.motherNum}`}
                   </p>
                 </div>
               </div>
             </div>

            {/* Messages area */}
            <div 
              className="flex-1 overflow-y-auto p-4"
              style={{
                backgroundImage: `url('/assets/chatbg.webp')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="flex flex-col gap-2 justify-end">
                {loading ? (
                  <div className="text-center text-gray-500">Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    No messages yet. Start the conversation!
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    const previousMsg = index > 0 ? messages[index - 1] : null;
                    const showDateSeparator = !previousMsg || isDifferentDay(
                      msg.time || (messages.find(m => m.$id === msg.$id) as any)?.$createdAt,
                      previousMsg.time || (messages.find(m => m.$id === previousMsg.$id) as any)?.$createdAt
                    );

                    return (
                      <React.Fragment key={msg.$id}>
                        {showDateSeparator && (
                          <DateSeparator 
                            label={getRelativeDateLabel(
                              msg.time || (messages.find(m => m.$id === msg.$id) as any)?.$createdAt
                            )} 
                          />
                        )}
                        <div className={`flex ${msg.isUser ? 'justify-start' : 'justify-end'}`}>
                          <div className="group relative">
                            <div
                              className={`max-w-xs rounded-lg px-4 py-2 shadow-md relative chat-bubble flex flex-col items-center ${
                                msg.isUser 
                                  ? 'bg-green-100 ml-auto' 
                                  : 'bg-white'
                              }`}
                              style={{ padding: msg.img ? '12px' : undefined }}
                            >
                              {/* Three-dot options icon for user messages */}
                              {msg.isUser && (
                                <div
                                  className="absolute bottom-1 left-[10px] z-10 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                  onClick={() => setOptionsOpenId(optionsOpenId === msg.$id ? null : msg.$id)}
                                >
                                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <circle cx="5" cy="12" r="2" fill="#555" />
                                    <circle cx="12" cy="12" r="2" fill="#555" />
                                    <circle cx="19" cy="12" r="2" fill="#555" />
                                  </svg>
                                </div>
                              )}
                              
                              {/* Options menu */}
                              {optionsOpenId === msg.$id && (
                                <div className="absolute bottom-7 left-[10px] bg-white border rounded shadow z-20 min-w-[80px]">
                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm hover:bg-red-100 text-red-600"
                                    onClick={async () => {
                                      await deleteParentMessage(msg.$id);
                                      setOptionsOpenId(null);
                                      loadMessages(selectedUser.userId);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                              
                              {/* Image and message content */}
                              {msg.img && (
                                <img
                                  src={msg.img}
                                  alt="attachment"
                                  className="block rounded-lg object-contain cursor-pointer mb-2"
                                  style={{ maxWidth: 320, maxHeight: 180, width: '100%' }}
                                  onClick={() => setFullscreenImg(msg.img!)}
                                />
                              )}
                              {msg.message && (
                                <div className="text-sm whitespace-pre-line w-full text-left">{msg.message}</div>
                              )}
                              <div className="text-xs text-gray-500 text-right mt-1 w-full">
                                {formatTime(msg.time || (messages.find(m => m.$id === msg.$id) as any)?.$createdAt)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Message input */}
            <form
              onSubmit={handleSend}
              className="bg-white border-t border-gray-200 p-4"
            >
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <label className="cursor-pointer flex items-center">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <span className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-full">📎</span>
                </label>
                {imagePreview && (
                  <div className="relative">
                    <img src={imagePreview} alt="preview" className="max-h-12 max-w-[72px] rounded shadow object-contain" style={{ aspectRatio: '4/3', background: '#eee' }} />
                    <button type="button" onClick={removeImage} className="absolute top-0 right-0 bg-red-500 text-white rounded-full px-1 py-0.5 text-xs">✕</button>
                  </div>
                )}
                <button
                  type="submit"
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-semibold shadow disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Send'}
                </button>
              </div>
            </form>
          </>
                 ) : (
           <div className="flex-1 flex items-center justify-center">
             <div className="text-center text-gray-500">
               <div className="text-6xl mb-4">💬</div>
               <h3 className="text-xl font-medium mb-2">Welcome to Parent Chat</h3>
               <p>Select a student from the list to chat with their parent</p>
             </div>
           </div>
                  )}
       </div>

       {/* New Chat Dialog */}
       {showNewChatDialog && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white rounded-lg shadow-xl w-96 max-h-[80vh] flex flex-col">
             <div className="p-4 border-b border-gray-200">
               <div className="flex items-center justify-between">
                 <h3 className="text-lg font-semibold text-gray-900">Start New Conversation</h3>
                 <button
                   onClick={() => {
                     setShowNewChatDialog(false);
                     setNewChatSearchQuery('');
                   }}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
               </div>
               <p className="text-sm text-gray-600 mt-1">Search for a student to start chatting with their parent</p>
             </div>
             
             <div className="p-4 border-b border-gray-200">
               <div className="relative">
                 <input
                   type="text"
                   placeholder="Search students..."
                   value={newChatSearchQuery}
                   onChange={(e) => setNewChatSearchQuery(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                 />
                 <svg className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                 </svg>
               </div>
             </div>
             
             <div className="flex-1 overflow-y-auto p-4">
               {filteredStudents.length === 0 ? (
                 <div className="text-center text-gray-500">
                   {newChatSearchQuery ? 'No students found' : 'Search for a student to start chatting'}
                 </div>
               ) : (
                 <div className="space-y-2">
                   {filteredStudents.map((student) => (
                     <div
                       key={student.userId}
                       onClick={() => startNewConversation(student)}
                       className="p-3 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                     >
                       <div className="flex items-center">
                         {student.studentPhotoUrl ? (
                           <img 
                             src={student.studentPhotoUrl} 
                             alt={student.studentName}
                             className="w-10 h-10 rounded-full object-cover"
                           />
                         ) : (
                           <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                             {student.studentName.charAt(0).toUpperCase()}
                           </div>
                         )}
                         <div className="ml-3 flex-1">
                           <h4 className="font-medium text-gray-900">{student.studentName}</h4>
                           <p className="text-sm text-gray-500">Level {student.level} • {student.group}</p>
                           <p className="text-xs text-gray-400">Parent: {student.fatherName}</p>
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
               )}
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default ParentChatPage;